import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import {
  Box, Button, Chip, Stack, Typography, alpha, CircularProgress, Menu, MenuItem,
} from '@mui/material';
import { ArrowForward, MoreHoriz } from '@mui/icons-material';
import { getWorkflowNextStatuses } from '../../services/businessService';
import { formatStatus } from './dispatchTypes';

interface DispatchStatusActionsProps {
  entityType: string;
  entityId: string;
  currentStatus: string;
  onStatusChange: (status: string) => Promise<void>;
}

export default function DispatchStatusActions({
  entityType,
  entityId,
  currentStatus,
  onStatusChange,
}: DispatchStatusActionsProps) {
  const [loading, setLoading] = useState<string | null>(null);
  const [moreAnchor, setMoreAnchor] = useState<null | HTMLElement>(null);

  const { data: workflow, isLoading } = useQuery({
    queryKey: ['workflow-transitions', entityType, currentStatus],
    queryFn: () => getWorkflowNextStatuses(entityType, currentStatus),
    enabled: !!entityType && !!currentStatus,
  });

  const stageLabels = Object.fromEntries(
    (workflow?.stages || []).map((s) => [s.key, s.label]),
  );

  const nextStatuses = workflow?.nextStatuses?.filter((s) => s !== currentStatus) || [];
  const primary = nextStatuses.find((s) => s !== 'cancelled') || nextStatuses[0];
  const secondary = nextStatuses.filter((s) => s !== primary);
  const cancelStatus = nextStatuses.find((s) => s === 'cancelled');

  const handleStatus = async (status: string) => {
    setLoading(status);
    setMoreAnchor(null);
    try {
      await onStatusChange(status);
    } finally {
      setLoading(null);
    }
  };

  if (isLoading) {
    return <CircularProgress size={20} />;
  }

  if (!nextStatuses.length) {
    return (
      <Chip
        label={stageLabels[currentStatus] || formatStatus(currentStatus)}
        size="small"
        sx={{ fontWeight: 700, bgcolor: alpha('#22c55e', 0.1), color: '#15803d' }}
      />
    );
  }

  return (
    <Box>
      <Stack direction="row" alignItems="center" spacing={1} mb={1.5}>
        <Typography variant="caption" fontWeight={700} color="text.secondary">CURRENT</Typography>
        <Chip
          label={stageLabels[currentStatus] || formatStatus(currentStatus)}
          size="small"
          variant="outlined"
          sx={{ fontWeight: 700, textTransform: 'capitalize' }}
        />
      </Stack>

      <Stack spacing={1}>
        {primary && (
          <Button
            fullWidth
            variant="contained"
            size="medium"
            disabled={!!loading}
            endIcon={loading === primary ? undefined : <ArrowForward />}
            onClick={() => handleStatus(primary)}
            sx={{ fontWeight: 700, py: 1, borderRadius: 2 }}
          >
            {loading === primary ? 'Updating…' : (stageLabels[primary] || formatStatus(primary))}
          </Button>
        )}

        <Stack direction="row" spacing={1}>
          {secondary.length > 0 && (
            <Button
              fullWidth
              variant="outlined"
              size="small"
              endIcon={<MoreHoriz />}
              onClick={(e) => setMoreAnchor(e.currentTarget)}
              disabled={!!loading}
              sx={{ fontWeight: 600 }}
            >
              More actions
            </Button>
          )}
          {cancelStatus && (
            <Button
              variant="outlined"
              color="error"
              size="small"
              disabled={!!loading}
              onClick={() => handleStatus(cancelStatus)}
              sx={{ fontWeight: 600, minWidth: 100 }}
            >
              {loading === cancelStatus ? '…' : 'Cancel'}
            </Button>
          )}
        </Stack>
      </Stack>

      <Menu anchorEl={moreAnchor} open={!!moreAnchor} onClose={() => setMoreAnchor(null)}>
        {secondary.map((status) => (
          <MenuItem key={status} onClick={() => handleStatus(status)} disabled={!!loading}>
            {stageLabels[status] || formatStatus(status)}
          </MenuItem>
        ))}
      </Menu>
    </Box>
  );
}
